import {
  AdminSpinner,
  AgentCustomersStatsVDriveResponse,
  AgentCustomerStatsResponse,
  ArrowRed,
  Box,
  displayCentsInDollars,
  Div,
  getCustomerStatsVDrive,
  H3,
  H5,
  Icon,
  IconButtonStyled,
  lightTheme,
  ModalPortal,
  VDriveLogoSmall,
} from '@vgrubs/components';
import VDriveStatItem from './VDriveStatItem';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export const fetchCustomerStatsVDrive = async ({
  pageParam = 1,
}: {
  pageParam: number;
}) => {
  const { data } = await getCustomerStatsVDrive({
    limit: 20,
    page: pageParam,
  });

  // If data length is less than the limit, we know there is no next page
  const nextPage = data.data.length === 20 ? pageParam + 1 : undefined;

  return {
    customerStatsVDrive: data ?? [],
    nextPage,
  };
};

const VDriveStats = ({ stats }: { stats: AgentCustomerStatsResponse }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bestPerformingLocations, setBestPerformingLocations] = useState<
    AgentCustomersStatsVDriveResponse[]
  >([]);

  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [ref, inView] = useInView();

  const getData = async () => {
    if (page !== 1) {
      setLoadingMore(true);
    }
    const data = await fetchCustomerStatsVDrive({
      pageParam: page,
    });

    try {
      if (data.nextPage) {
        setHasMore(true);
        setPage(data.nextPage);
      } else {
        setHasMore(false);
      }
      if (data.customerStatsVDrive.data.length > 0) {
        setBestPerformingLocations((prev) => [
          ...prev,
          ...data.customerStatsVDrive.data,
        ]);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getData();
  }, []);

  useEffect(() => {
    if (inView && hasMore) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <>
      <Box $p="16px" $flex $column>
        <Div $flex $alignCenter $gap="8px" $between $p="0 0 16px 0">
          <Div $flex $alignCenter $gap="8px">
            <Icon $size={40} $sizeSM={40}>
              <VDriveLogoSmall />
            </Icon>
            <H3 $m="0">vDrive</H3>
          </Div>
          <IconButtonStyled onClick={() => setIsOpen(true)}>
            <Icon $size={24} $sizeSM={24}>
              <ArrowRed />
            </Icon>
          </IconButtonStyled>
        </Div>
        <Div $flex $column $gap="8px" style={{ flex: 1 }}>
          {stats?.vDrive.map((stat) => (
            <VDriveStatItem key={stat.locationId} location={stat} />
          ))}
        </Div>
        <Div $flex $alignCenter $justifyEnd $gap="8px" $mt="16" $mb="4">
          <H5 $bold $mb="0">
            Restaurant Gains:
          </H5>
          <H5 $m="0" $right style={{ flex: 'none' }}>
            <span style={{ color: lightTheme.green }}>
              {displayCentsInDollars(stats?.vDriveTotal || 0)}
            </span>
          </H5>
        </Div>
      </Box>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        $numOfFloatingButtons={1}
        logo={false}
        width="600px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit={'vDrive'}
        footer={
          <Div $flex $alignEnd $justifyEnd $gap="8px" $mt="50">
            <H5 $bold $mb="0">
              Restaurant Gains:
            </H5>
            <H5 $m="0" $right style={{ flex: 'none' }}>
              <span style={{ color: lightTheme.green }}>
                {displayCentsInDollars(stats?.vDriveTotal || 0)}
              </span>
            </H5>
          </Div>
        }
      >
        {loadingMore && <AdminSpinner small absolute />}
        <Div $flex $column $gap="12px">
          {bestPerformingLocations?.map((item) => (
            <VDriveStatItem key={item.locationId} location={item} />
          ))}
        </Div>

        <Div $height="20px" ref={ref}></Div>
      </ModalPortal>
    </>
  );
};

export { VDriveStats };
